import React, { useMemo } from "react";
import { Box, Button, Divider, Stack } from "@mui/material";

import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useRisk } from "../hooks/useRisk";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { filterCommercialCategory } from "../../../shared/utils/filterCommercialCategory";
import { useGetJeopardyStakeholders } from "../../../shared/hooks";

export const RaiseRisk: React.FC<{ editStatus: boolean, riskId: string | null, projectId: string | undefined, onCancel: () => void; refetch: () => void; }> = ({ editStatus, riskId, projectId, onCancel, refetch }) => {

    const { getFilterOptions } = useDisplayField([]);
    const { isCommercialPerson, isAdminPerson } = useCapabilities();

    const {
        overviewData,
        overViewDataErrors, handleOnChange,
        isSaving: saving, onSaveRisk,
        isPhasePending, phaseOptions
    } = useRisk({ editStatus, riskId, projectId, refetch: () => { refetch(); onCancel(); } });

    const params = () => {
        if (projectId != undefined) {
            return { project_id: projectId };
        } else if (overviewData.type === "Project" && overviewData.project?.id) {
            return { project_id: overviewData.project.id };
        } else if (overviewData.type === "Programme" && overviewData.programme?.id) {
            return { programme_id: overviewData.programme.id };
        } else if (overviewData.type === "Portfolio" && overviewData.portfolio?.id) {
            return { portfolio_id: overviewData.portfolio.id };
        }
        return {};
    };

    const { data: stakeholders, isLoading: isLoadingStakeholders, refetch: refetchStakeholders } = useGetJeopardyStakeholders({ params: params() });

    const userOptions = useMemo(() => {
        if (!stakeholders) return [];
        return stakeholders?.data?.flatMap((role: any) =>
        role?.users?.map((user: any) => ({
            label: user.user_name,
            value: user.user_id
        }))) || [];
    },[stakeholders]);

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'} overflow={'hidden'}>
            <Box flex={1} sx={{ overflowY: 'auto' }} p={2}>
                <Box display={'flex'} flexDirection={'column'} gap={2}>

                    <FieldControl
                        keyName={'type'}
                        label={"Risk Type"}
                        variant={"select"}
                        options={[
                            { label: 'Project', value: 'Project' },
                            { label: 'Portfolio', value: 'Portfolio' },
                            { label: 'Programme', value: 'Programme' },
                        ]}
                        value={overviewData.type}
                        onChange={handleOnChange}
                        required
                         disabled={!!projectId}   
                        error={!!overViewDataErrors.type}
                        helperText={overViewDataErrors.type}
                    />

                    {
                        overviewData.type === 'Project' &&
                        <ControlledAutocomplete
                            type="PROJECT"
                            value={overviewData.project}
                            onSelect={(val: any) => handleOnChange("project", val)}
                            label="Search project"
                            placeholder="Search project"
                            required
                            disabled={!!projectId}        
                            error={!!overViewDataErrors.project}
                            helperText={overViewDataErrors.project}
                        />
                    }

                    {
                        overviewData.type === 'Project' &&
                        <FieldControl
                            keyName={'phase'}
                            label={"Select Project Phase"}
                            variant={"select"}
                            value={overviewData.phase}
                            options={
                                phaseOptions.map((value) => {
                                    return {
                                        value: value.id, label: value.name
                                    }
                                })
                            }
                        onChange={handleOnChange}
                            loading={isPhasePending}
                            required
                            error={!!overViewDataErrors.phase}
                            helperText={overViewDataErrors.phase}
                        />
                    }

                    {
                        overviewData.type === 'Portfolio' &&
                        <ControlledAutocomplete
                            type="PORTFOLIO"
                            value={overviewData.portfolio}
                        onSelect={(val: any) => handleOnChange("portfolio", val)}
                            label="Search portfolio"
                            placeholder="Search portfolio"
                            required
                            error={!!overViewDataErrors.portfolio}
                            helperText={overViewDataErrors.portfolio}
                        />
                    }

                    {
                        overviewData.type === 'Programme' &&
                        <ControlledAutocomplete
                            type="PROGRAMME"
                            value={overviewData.programme}
                        onSelect={(val: any) => handleOnChange("programme", val)}
                            label="Search programme"
                            placeholder="Search programme"
                            required
                            error={!!overViewDataErrors.programme}
                            helperText={overViewDataErrors.programme}
                        />
                    }

                    <Divider />

                    <FieldControl
                        keyName={"riskTitle"}
                        label={"Title"}
                        variant={"input"}
                        value={overviewData.riskTitle}
                    onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.riskTitle}
                        helperText={overViewDataErrors.riskTitle}
                    />

                    <FieldControl
                        keyName={"riskDescription"}
                        label={"Risk Description"}
                        variant={"input"}
                        multiline
                        value={overviewData.riskDescription}
                    onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.riskDescription}
                        helperText={overViewDataErrors.riskDescription}
                    />

                    <FieldControl
                        keyName={"riskCategory"}
                        label={"Category"}
                        variant={"select"}
                        options={filterCommercialCategory({ domain: "RISK_CATEGORY", filterValue: "Commercial", isInclude: isCommercialPerson, isAdminPerson }).map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        value={overviewData.riskCategory}
                    onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.riskCategory}
                        helperText={overViewDataErrors.riskCategory}

                    />

                    <FieldControl
                        keyName={"riskStatus"}
                        label={"Status"}
                        variant={"select"}
                        options={getFilterOptions('RISK_OVERVIEW_STATUS').map((value) => {
                            return {
                                label: value.display_text,
                                value: value.key
                            }
                        })}
                        value={overviewData.riskStatus}
                    onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.riskStatus}
                        helperText={overViewDataErrors.riskStatus}
                    />

                    <FieldControl
                        keyName="assigned"
                        label="Assigned To"
                        variant={"select"}
                        options={userOptions}
                        loading={isLoadingStakeholders}
                        value={overviewData.assigned}
                        onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.assigned}
                        helperText={overViewDataErrors.assigned}
                    />

                    <FieldControl
                        keyName={"riskReviewDate"}
                        label={"Review Date"}
                        variant={"datetime"}
                        value={overviewData.riskReviewDate}
                    onChange={handleOnChange}
                        required
                        error={!!overViewDataErrors.riskReviewDate}
                        helperText={overViewDataErrors.riskReviewDate}
                    />

                </Box>
            </Box>
            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button onClick={onCancel} variant="outlined" loading={saving} disabled={saving}>Cancel</Button>
                <Button onClick={onSaveRisk} variant="contained" loading={saving} disabled={saving}>Save</Button>
            </Stack>
        </Box>
    );

};